import type {
  IRoutineRepository,
  ListRoutinesQuery,
  CreateRoutineInput,
  UpdateRoutineInput,
} from '@yawi/domain';
import { paginateArray } from '@yawi/domain';
import type {
  Routine,
  RoutineActionCatalogEntry,
  RoutineCronValidationResult,
} from '@yawi/types';

const now = new Date().toISOString();

const ACTION_CATALOG: RoutineActionCatalogEntry[] = [
  {
    actionCode: 'billing.generate_monthly_charges',
    category: 'billing',
    displayName: 'Generar cargos mensuales',
    description: 'Crea los cargos recurrentes de mantenimiento para todas las unidades activas.',
    hasParams: true,
    paramsSchema: [
      { key: 'dayOfMonth', label: 'Día del mes', type: 'number', required: false },
      { key: 'includeFixedFees', label: 'Incluir cuotas fijas', type: 'boolean', required: false },
    ],
  },
  {
    actionCode: 'billing.apply_late_fees',
    category: 'billing',
    displayName: 'Aplicar recargos por mora',
    description: 'Aplica recargos a cargos vencidos según la política configurada.',
    hasParams: true,
    paramsSchema: [
      { key: 'gracePeriodDays', label: 'Días de gracia', type: 'number', required: false },
      { key: 'percentage', label: 'Porcentaje de recargo', type: 'number', required: false },
    ],
  },
  {
    actionCode: 'billing.send_payment_reminders',
    category: 'billing',
    displayName: 'Enviar recordatorios de pago',
    description: 'Notifica a residentes con pagos próximos a vencer o vencidos.',
    hasParams: true,
    paramsSchema: [
      { key: 'channels', label: 'Canales', type: 'select', required: false, options: [
        { value: 'email', label: 'Email' },
        { value: 'push', label: 'Push' },
      ]},
    ],
  },
  {
    actionCode: 'billing.mark_overdue_invoices',
    category: 'billing',
    displayName: 'Marcar facturas vencidas',
    description: 'Cambia el estado a OVERDUE de las facturas con fecha de vencimiento pasada.',
    hasParams: false,
  },
  {
    actionCode: 'access.cleanup_logs',
    category: 'access',
    displayName: 'Limpiar logs de acceso',
    description: 'Elimina logs de acceso más antiguos que el período de retención configurado.',
    hasParams: true,
    paramsSchema: [
      { key: 'retentionDays', label: 'Días de retención', type: 'number', required: false },
    ],
  },
  {
    actionCode: 'restrictions.expire_expired',
    category: 'restrictions',
    displayName: 'Expirar restricciones vencidas',
    description: 'Marca como expiradas las restricciones cuya fecha límite ya pasó.',
    hasParams: false,
  },
  {
    actionCode: 'maintenance.cleanup_routine_executions',
    category: 'maintenance',
    displayName: 'Limpiar historial de rutinas',
    description: 'Purga el historial de ejecuciones de rutinas más antiguo que el período de retención.',
    hasParams: true,
    paramsSchema: [
      { key: 'retentionDays', label: 'Días de retención', type: 'number', required: false },
    ],
  },
  {
    actionCode: 'http_webhook',
    category: 'custom',
    displayName: 'Webhook HTTP',
    description: 'Envía una solicitud HTTP a una URL externa al ejecutarse.',
    hasParams: true,
    paramsSchema: [
      { key: 'url', label: 'URL', type: 'text', required: true },
      { key: 'method', label: 'Método', type: 'select', required: true, options: [
        { value: 'GET', label: 'GET' },
        { value: 'POST', label: 'POST' },
        { value: 'PUT', label: 'PUT' },
      ]},
      { key: 'timeoutSeconds', label: 'Timeout (segundos)', type: 'number', required: false },
    ],
  },
];

const MOCK_ROUTINES: Routine[] = [
  {
    id: 'routine-001',
    tenantId: 'tenant-demo',
    code: 'billing-generate-monthly-charges',
    name: 'Generación de cargos mensuales',
    description: 'Crea los cargos recurrentes el día 1 de cada mes.',
    category: 'billing',
    action: 'billing.generate_monthly_charges',
    params: { dayOfMonth: 1, includeFixedFees: true },
    cronExpression: '0 2 1 * *',
    timezone: 'America/Mexico_City',
    enabled: true,
    isSystem: true,
    status: 'idle',
    triggerType: 'monthly',
    triggerSummary: 'Mensual · Día 1 · 02:00',
    lastRunAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    lastStatus: 'success',
    lastDurationMs: 4500,
    nextRunAt: new Date(Date.now() + 26 * 24 * 60 * 60 * 1000).toISOString(),
    failureCount: 0,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'routine-002',
    tenantId: 'tenant-demo',
    code: 'billing-send-payment-reminders',
    name: 'Recordatorios de pago',
    description: 'Notifica a residentes con pagos vencidos o próximos a vencer.',
    category: 'billing',
    action: 'billing.send_payment_reminders',
    params: { channels: ['email', 'push'] },
    cronExpression: '0 9 * * *',
    timezone: 'America/Mexico_City',
    enabled: false,
    isSystem: true,
    status: 'idle',
    triggerType: 'daily',
    triggerSummary: 'Diario · 09:00',
    lastRunAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    lastStatus: 'success',
    lastDurationMs: 1200,
    nextRunAt: undefined,
    failureCount: 0,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'routine-003',
    tenantId: 'tenant-demo',
    code: 'access-cleanup-logs',
    name: 'Limpieza de logs de acceso',
    description: 'Elimina logs de acceso con más de 90 días de antigüedad.',
    category: 'access',
    action: 'access.cleanup_logs',
    params: { retentionDays: 90 },
    cronExpression: '0 1 * * 0',
    timezone: 'America/Mexico_City',
    enabled: true,
    isSystem: true,
    status: 'idle',
    triggerType: 'weekly',
    triggerSummary: 'Semanal · Domingo · 01:00',
    lastRunAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    lastStatus: 'failed',
    lastDurationMs: undefined,
    nextRunAt: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
    failureCount: 2,
    createdAt: now,
    updatedAt: now,
  },
];

let mockRoutines = [...MOCK_ROUTINES];
let nextId = 100;

export class MockRoutineRepository implements IRoutineRepository {
  async list(query?: ListRoutinesQuery) {
    let items = [...mockRoutines];

    if (query?.category) {
      items = items.filter(r => r.category === query.category);
    }
    if (query?.enabled !== undefined) {
      items = items.filter(r => r.enabled === query.enabled);
    }
    if (query?.search) {
      const q = query.search.toLowerCase();
      items = items.filter(r =>
        r.name.toLowerCase().includes(q) ||
        r.action.toLowerCase().includes(q),
      );
    }

    return paginateArray(items, query?.page ?? 1, query?.limit ?? 10);
  }

  async findById(id: string) {
    return mockRoutines.find(r => r.id === id) ?? null;
  }

  async create(input: CreateRoutineInput): Promise<Routine> {
    const action = ACTION_CATALOG.find(a => a.actionCode === input.action);
    const routine: Routine = {
      id: `routine-${++nextId}`,
      tenantId: 'tenant-demo',
      code: input.name.toLowerCase().replace(/\s+/g, '-'),
      name: input.name,
      description: input.description,
      category: input.category,
      action: input.action,
      params: input.params ?? {},
      cronExpression: input.cronExpression,
      timezone: input.timezone ?? 'America/Mexico_City',
      enabled: input.isActive ?? true,
      isSystem: false,
      status: 'idle',
      triggerType: 'custom_cron',
      triggerSummary: input.cronExpression,
      failureCount: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    mockRoutines.push(routine);
    return routine;
  }

  async update(id: string, input: UpdateRoutineInput): Promise<Routine> {
    const idx = mockRoutines.findIndex(r => r.id === id);
    if (idx === -1) throw new Error('Routine not found');
    const current = mockRoutines[idx];
const { isActive, ...rest } = input;
    const updated: Routine = {
      ...current,
      ...rest,
      ...(isActive !== undefined ? { enabled: isActive } : {}),
      // Cuando cambia el cron, el triggerSummary del backend queda obsoleto;
      // se resetea al cron para que la lista derive el texto de nuevo.
      ...(rest.cronExpression && rest.cronExpression !== current.cronExpression
        ? { triggerSummary: rest.cronExpression, triggerType: 'custom_cron' as const }
        : {}),
      updatedAt: new Date().toISOString(),
    };
    mockRoutines[idx] = updated;
    return updated;
  }

  async delete(id: string): Promise<void> {
    const routine = mockRoutines.find(r => r.id === id);
    if (routine?.isSystem) throw new Error('Cannot delete system routine');
    mockRoutines = mockRoutines.filter(r => r.id !== id);
  }

  async toggle(id: string, enabled: boolean): Promise<Routine> {
    return this.update(id, { isActive: enabled });
  }

  async trigger(id: string): Promise<{ executionId: string }> {
    return { executionId: `exec-manual-${Date.now()}` };
  }

  async validateCron(cronExpression: string): Promise<RoutineCronValidationResult> {
    const parts = cronExpression.trim().split(/\s+/);
    if (parts.length !== 5) {
      return { valid: false, errors: ['La expresión cron debe tener 5 campos: minuto hora día-mes mes día-semana'] };
    }
    const nextRuns = Array.from({ length: 5 }, (_, i) => {
      const d = new Date(Date.now() + (i + 1) * 24 * 60 * 60 * 1000);
      return d.toISOString();
    });
    return { valid: true, nextRuns };
  }

  async listActions(): Promise<RoutineActionCatalogEntry[]> {
    return ACTION_CATALOG;
  }
}
